import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { onValue, ref } from "firebase/database";

import { db } from "../../config/firebase";
import { openKitchenSession, listKitchenOrders, setOrderStatus } from "./kitchenApi";
import "./kitchen-display.css";
import { parseApiDate } from '../../utils/datetime';

/**
 * QR Loyalty & Ordering — Phase 3. Standalone Kitchen Display.
 *
 * A public, PIN-gated page (/kitchen/:venueCode on THIS SPA's host, e.g.
 * app.popuppush.com — NOT the popuppush.com apex, which is the marketing
 * site) the kitchen leaves
 * open all shift. After the PIN check it becomes a real-time subscriber on
 * RTDB Loyalty/{company_id}/orders (env-prefixed), painting three columns
 * New → Preparing → Ready. A REST reconcile runs on an interval as a safety net
 * (RTDB can be blocked on some networks). A bell rings on every new order.
 * Visuals are copied 1:1 from the "Scan & Stay" prototype (artifact 791b3e9d).
 */

const OVERDUE_MIN = 15;
const sessKey = (code) => `kds_sess::${String(code).toLowerCase()}`;
const STATUS = { NEW: "new", PREP: "preparing", READY: "ready" };

export default function KitchenDisplay() {
  const { venueCode } = useParams();
  const { t, i18n } = useTranslation();
  const isAr = (i18n.resolvedLanguage || i18n.language || "en").startsWith("ar");

  const [session, setSession] = useState(null); // { token, companyId, serviceType, venueName, exp }
  const [pin, setPin] = useState("");
  const [gateErr, setGateErr] = useState("");
  const [gateBusy, setGateBusy] = useState(false);

  const [orders, setOrders] = useState([]);
  const [busyIds, setBusyIds] = useState({}); // id -> true while a status write is in flight
  const [notice, setNotice] = useState("");   // transient banner (rate limit / write failure)
  const [muted, setMuted] = useState(false);
  const [tick, setTick] = useState(0); // drives the elapsed-time re-render + clock

  const seenRef = useRef(new Set()); // order ids we've already seen (bell de-dupe)
  const initedRef = useRef(false);   // skip the bell on the first snapshot
  // In-flight optimistic moves: id -> next status (or "__removed__"). Protects an
  // optimistic move from being reverted by a stale poll/RTDB tick that arrives
  // before the backend has written the change. Self-heals once the server agrees.
  const pendingRef = useRef(new Map());
  const audioRef = useRef(null);
  const mutedRef = useRef(false);
  useEffect(() => { mutedRef.current = muted; }, [muted]);

  /* ── restore a still-valid session on load ─────────────────────────────── */
  useEffect(() => {
    try {
      const s = JSON.parse(sessionStorage.getItem(sessKey(venueCode)) || "null");
      if (s?.token && s?.exp > Date.now()) setSession(s);
    } catch (_) {}
  }, [venueCode]);

  /* ── the bell (WebAudio; unlocked by the PIN-submit gesture) ────────────── */
  const beep = useCallback(() => {
    if (mutedRef.current) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ac = audioRef.current || (audioRef.current = new AC());
      if (ac.state === "suspended") ac.resume();
      // Two short honey-warm chimes.
      [0, 0.16].forEach((offset, i) => {
        const osc = ac.createOscillator();
        const gain = ac.createGain();
        osc.type = "sine";
        osc.frequency.value = i === 0 ? 880 : 1174;
        gain.gain.setValueAtTime(0.0001, ac.currentTime + offset);
        gain.gain.exponentialRampToValueAtTime(0.25, ac.currentTime + offset + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + offset + 0.22);
        osc.connect(gain).connect(ac.destination);
        osc.start(ac.currentTime + offset);
        osc.stop(ac.currentTime + offset + 0.24);
      });
    } catch (_) {}
  }, []);

  /* ── merge a fresh order list into state + ring on genuinely-new tickets ── */
  const applyOrders = useCallback((list) => {
    let arr = (list || []).slice();

    // Respect in-flight optimistic moves so a stale poll/RTDB tick can't revert
    // a card the operator just advanced. Clear each override once the server has
    // caught up (self-healing), then overlay whatever is still pending.
    const pending = pendingRef.current;
    if (pending.size) {
      for (const [id, want] of [...pending]) {
        const srv = arr.find((o) => o.id === id);
        if (want === "__removed__" ? !srv : srv && srv.status === want) pending.delete(id);
      }
      arr = arr
        .filter((o) => pending.get(o.id) !== "__removed__")
        .map((o) => {
          const want = pending.get(o.id);
          return want && want !== "__removed__" ? { ...o, status: want } : o;
        });
    }

    // Oldest ticket first. `created_at` arrives as a millisecond epoch NUMBER
    // (Carbon::valueOf()) on both the REST payload and the RTDB mirror —
    // parseApiDate handles that explicitly. It briefly did not, and every value
    // collapsed to `?? 0`, so this comparator silently stopped ordering anything.
    arr.sort((a, b) => (parseApiDate(a.created_at)?.getTime() ?? 0)
                     - (parseApiDate(b.created_at)?.getTime() ?? 0));
    if (initedRef.current) {
      const fresh = arr.some((o) => o.status === STATUS.NEW && !seenRef.current.has(o.id));
      if (fresh) beep();
    }
    arr.forEach((o) => seenRef.current.add(o.id));
    initedRef.current = true;
    // Forget ids that left the board so a returning number can ring again.
    const live = new Set(arr.map((o) => o.id));
    seenRef.current.forEach((id) => { if (!live.has(id)) seenRef.current.delete(id); });
    setOrders(arr);
  }, [beep]);

  const normalizeSnapshot = (val) =>
    val ? Object.entries(val).map(([id, o]) => ({ id, ...o })) : [];

  /* ── live feed: RTDB primary + REST reconcile safety net ───────────────── */
  useEffect(() => {
    if (!session?.companyId) return;
    let alive = true;

    // Seed instantly (RTDB may take a moment / be blocked).
    listKitchenOrders(session.token).then((rows) => { if (alive) applyOrders(rows); }).catch(() => {});

    let unsub = () => {};
    if (db) {
      const path = `${process.env.REACT_APP_APP_ENV}/Loyalty/${session.companyId}/orders`;
      unsub = onValue(
        ref(db, path),
        (snap) => { if (alive) applyOrders(normalizeSnapshot(snap.val())); },
        (err) => { console.warn("[kitchen] RTDB listener error:", err?.code || err); }
      );
    }

    // Reconcile in case the RTDB socket is blocked/dropped (anonymous auth can
    // fail on some mobile networks). Short interval so the board stays near
    // real-time even without RTDB; optimistic updates cover the operator's taps.
    //
    // Backs off on 429 instead of hammering: a throttled poll used to burn the
    // budget that the operator's next status tap needed, so the board polled
    // itself out of being usable. `skip` counts ticks to sit out.
    let skip = 0;
    const poll = setInterval(() => {
      if (skip > 0) { skip -= 1; return; }
      listKitchenOrders(session.token)
        .then((rows) => { if (alive) { applyOrders(rows); setNotice(""); } })
        .catch((err) => {
          if (err?.response?.status === 429) {
            skip = 8;                       // ~1 minute off the gas
            if (alive) setNotice(t("kitchen.rate_limited"));
          }
        });
    }, 7000);

    return () => { alive = false; unsub(); clearInterval(poll); };
  }, [session, applyOrders, t]);

  /* ── 1s clock / elapsed-timer heartbeat ────────────────────────────────── */
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  /* ── PIN submit ────────────────────────────────────────────────────────── */
  const submitPin = async (e) => {
    e.preventDefault();
    if (pin.length < 4) return setGateErr(t("kitchen.err_pin_short"));
    setGateBusy(true);
    setGateErr("");
    beep(); // unlock the audio context on this user gesture (muted->silent)
    try {
      const res = await openKitchenSession(venueCode, pin);
      const s = {
        token: res.token,
        companyId: res.company_id,
        serviceType: res.service_type,
        venueName: res.venue_name,
        currencySymbol: res.currency_symbol || "",
        exp: Date.now() + (res.expires_in || 3600) * 1000,
      };
      try { sessionStorage.setItem(sessKey(venueCode), JSON.stringify(s)); } catch (_) {}
      initedRef.current = false;
      seenRef.current = new Set();
      if (Array.isArray(res.orders)) applyOrders(res.orders);
      setSession(s);
    } catch (err) {
      const code = err?.response?.data?.error;
      setGateErr(code === "invalid_pin" ? t("kitchen.err_pin") : t("kitchen.err_venue"));
    } finally {
      setGateBusy(false);
    }
  };

  const exit = () => {
    try { sessionStorage.removeItem(sessKey(venueCode)); } catch (_) {}
    setSession(null);
    setOrders([]);
    setPin("");
  };

  /* ── advance a ticket ──────────────────────────────────────────────────── */
  const advance = async (order, to) => {
    if (busyIds[order.id]) return;              // double-tap guard
    const removed = to === "picked_up" || to === "cancelled";
    // Optimistic: reflect the move INSTANTLY for the operator who tapped, so the
    // board never waits on an RTDB echo or a poll. The pending override keeps a
    // stale tick from reverting it; it self-heals once the server agrees.
    pendingRef.current.set(order.id, removed ? "__removed__" : to);
    setBusyIds((b) => ({ ...b, [order.id]: true }));
    setOrders((os) =>
      removed
        ? os.filter((o) => o.id !== order.id)
        : os.map((o) => (o.id === order.id ? { ...o, status: to } : o))
    );
    try {
      await setOrderStatus(session.token, order.id, to);
      setNotice("");
      // Leave the override in place — applyOrders clears it when the next
      // server tick confirms the change, avoiding any flicker in between.
    } catch (err) {
      pendingRef.current.delete(order.id);      // give up the optimistic state
      const code = err?.response?.status;
      if (code === 401) return exit();          // token expired → re-PIN
      // Rate-limited. This used to be indistinguishable from success: the write
      // was rejected, the reconcile below was rejected too, and the card sat in
      // its optimistic position until someone reloaded and found the ticket back
      // at "new". Say so, and put the card back where the server has it.
      if (code === 429) setNotice(t("kitchen.rate_limited"));
      else setNotice(t("kitchen.update_failed"));
      // Undo the optimistic move locally FIRST — a `removed` card was filtered
      // out of the list, so restore it rather than only rewriting its status.
      // The reconcile below is authoritative, but it can be rate-limited too,
      // and a ticket must never silently vanish from the board.
      setOrders((os) => {
        const restored = os.some((o) => o.id === order.id)
          ? os.map((o) => (o.id === order.id ? { ...o, status: order.status } : o))
          : [...os, order];
        return restored.sort((a, b) => (parseApiDate(a.created_at)?.getTime() ?? 0)
                                     - (parseApiDate(b.created_at)?.getTime() ?? 0));
      });
      listKitchenOrders(session.token)
        .then((rows) => applyOrders(rows))
        .catch(() => {});
    } finally {
      setBusyIds((b) => { const n = { ...b }; delete n[order.id]; return n; });
    }
  };

  const toggleLang = () => i18n.changeLanguage(isAr ? "en" : "ar");

  const localItem = useCallback(
    (o) => (isAr && o.item_name_ar ? o.item_name_ar : o.item_name),
    [isAr]
  );

  /**
   * WHERE the food goes. Read off the ORDER, never the venue's current service
   * type — that was the bug: this asked `session.serviceType === "table"` first,
   * so the moment an owner flipped the venue to counter service the kitchen
   * stopped showing table numbers on tickets that genuinely had them. An order
   * placed while the venue was in table mode still belongs to table 2 forever,
   * and the cashier board has always read it that way; the kitchen disagreeing
   * meant the two screens described the same ticket differently and the food had
   * nowhere to go.
   *
   * Always rendered, never null. "Counter" is real information — it tells the
   * kitchen this one is collected rather than carried — and an absent chip is
   * indistinguishable from a chip that failed to render.
   */
  const whereChip = useCallback(
    (o) => (o.table_number
      ? `🍽️ ${t("kitchen.table")} ${o.table_number}`
      : `🏪 ${t("kitchen.counter")}`),
    [t]
  );

  /** WHO to call. Independent of where — counter tickets need both. */
  const nameChip = useCallback((o) => (o.name ? `👤 ${o.name}` : null), []);

  const elapsed = useCallback((o) => {
    // `Date.now() - o.created_at` subtracted a number from a STRING, which is
    // NaN — so every ticket rendered "NaN min" and nothing ever went overdue.
    const placed = parseApiDate(o.created_at);
    if (!placed) return { text: "", warn: false };
    const mins = Math.floor((Date.now() - placed.getTime()) / 60000);
    const warn = mins >= OVERDUE_MIN;
    if (mins < 1) return { text: t("kitchen.just_now"), warn: false };
    return { text: `${mins} ${t("kitchen.min")}`, warn };
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps

  const cols = useMemo(() => ({
    [STATUS.NEW]: orders.filter((o) => o.status === STATUS.NEW),
    [STATUS.PREP]: orders.filter((o) => o.status === STATUS.PREP),
    [STATUS.READY]: orders.filter((o) => o.status === STATUS.READY),
  }), [orders]);

  const clock = useMemo(() => {
    try {
      return new Date().toLocaleTimeString(isAr ? "ar" : "en", { hour: "numeric", minute: "2-digit" });
    } catch (_) { return ""; }
  }, [tick, isAr]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── PIN gate ──────────────────────────────────────────────────────────── */
  if (!session) {
    return (
      <div className="kds-page" dir={isAr ? "rtl" : "ltr"}>
        <div className="kds-gate">
          <form className="kds-gate-card" onSubmit={submitPin}>
            <div className="glyph">🍳</div>
            <h1>{t("kitchen.gate_title")}</h1>
            <div className="sub">{t("kitchen.gate_sub")}</div>
            <input
              className="kds-pin mono"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={8}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              placeholder="••••"
              aria-label={t("kitchen.pin_label")}
              autoFocus
            />
            {gateErr ? <div className="kds-gate-err">{gateErr}</div> : null}
            <button className="kds-gate-btn" type="submit" disabled={gateBusy}>
              {gateBusy ? t("kitchen.opening") : t("kitchen.open")}
            </button>
            <button type="button" className="kds-lang" onClick={toggleLang}>
              {isAr ? "English" : "العربية"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  /* ── the board ─────────────────────────────────────────────────────────── */
  const column = (statusKey, dotClass, label, ticketClass, btn) => {
    const items = cols[statusKey];
    return (
      <div>
        <div className="kcol-h" style={{ color: `var(--${dotClass})` }}>
          <span className={`dot ${dotClass}`} />
          {label} <span className="count mono">{items.length}</span>
        </div>
        <div className="kcol">
          {items.length === 0 ? <div className="kcol-empty">{t("kitchen.empty")}</div> : null}
          {items.map((o) => {
            const el = elapsed(o);
            const where = whereChip(o);
            const who = nameChip(o);
            const over = ticketClass === "new" || ticketClass === "prep" ? el.warn : false;
            return (
              <div key={o.id} className={`ticket ${ticketClass}${statusKey === STATUS.NEW ? " pulse" : ""}${over ? " over" : ""}`}>
                <div className="tk-top">
                  <span className="tk-no">#{o.no}</span>
                  <span className={`tk-time${el.warn ? " warn" : ""}`}>{el.text}</span>
                </div>
                <div className="tk-chips">
                  <span className={`tk-where${o.table_number ? " table" : ""}`}>{where}</span>
                  {who ? <span className="tk-who">{who}</span> : null}
                </div>
                <div className="tk-items">
                  <b>{o.qty}×</b> {localItem(o)}
                  {o.notes ? <><br /><span style={{ opacity: 0.7 }}>“{o.notes}”</span></> : null}
                </div>
                {o.total != null && (
                  <div className="tk-total mono">{session.currencySymbol} {Number(o.total).toFixed(2)}</div>
                )}
                <button
                  className={`tk-btn ${btn.cls}${busyIds[o.id] ? " busy" : ""}`}
                  disabled={!!busyIds[o.id]}
                  onClick={() => advance(o, btn.to)}
                >
                  {busyIds[o.id]
                    ? <><span className="tk-spin" /> {t("kitchen.processing")}</>
                    : btn.label}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="kds-page" dir={isAr ? "rtl" : "ltr"}>
      <div className="kds">
        <div className="kds-top">
          <div className="title">{session.venueName} · {t("kitchen.title")}</div>
          <div className="meta">
            <button type="button" onClick={() => setMuted((m) => !m)}>
              {muted ? "🔕 " + t("kitchen.sound_off") : "🔔 " + t("kitchen.sound_on")}
            </button>
            <span>{t(session.serviceType === "table" ? "kitchen.table_service" : "kitchen.counter_service")}</span>
            <span className="mono">{clock}</span>
            <button type="button" onClick={toggleLang}>{isAr ? "EN" : "ع"}</button>
            <button type="button" onClick={exit}>{t("kitchen.exit")}</button>
          </div>
        </div>

        {notice && (
          <div className="kds-notice" role="status">
            <span>⚠️ {notice}</span>
            <button type="button" onClick={() => setNotice("")} aria-label={t("kitchen.dismiss")}>×</button>
          </div>
        )}

        <div className="kds-cols">
          {column(STATUS.NEW, "coral", t("kitchen.col_new"), "new",
            { cls: "start", to: "preparing", label: "▶ " + t("kitchen.start") })}
          {column(STATUS.PREP, "honey", t("kitchen.col_preparing"), "prep",
            { cls: "mark", to: "ready", label: "✓ " + t("kitchen.mark_ready") })}
          {column(STATUS.READY, "jade", t("kitchen.col_ready"), "ready",
            { cls: "done", to: "picked_up", label: t("kitchen.picked_up") })}
        </div>

        <div className="kds-legend">
          <span className="lg"><span className="swatch" style={{ background: "var(--coral)" }} />{t("kitchen.col_new")}</span>
          <span className="lg"><span className="swatch" style={{ background: "var(--honey)" }} />{t("kitchen.col_preparing")}</span>
          <span className="lg"><span className="swatch" style={{ background: "var(--jade)" }} />{t("kitchen.col_ready")}</span>
          <span className="lg">⏱️ {t("kitchen.overdue_legend")}</span>
        </div>
      </div>
    </div>
  );
}
